import { toNullableBoolean, toNullableNumber, toNullableString } from '~/utils/coerce-nullable';
import { AI_VALIDATION_API_UTILITY_BILL_URL } from '~/utils/constants';
import { store } from '~/utils/store';
import { getUtilityBillExtractionMessage } from './messages';

export interface ExtractUtilityBillPage {
  readonly base64Image: string;
  readonly file?: File;
  readonly fileName: string;
  readonly mimeType: string;
}

export interface ExtractUtilityBillParams {
  readonly accountId?: number;
  readonly expectedCustomerName?: string;
  readonly expectedUtilityName?: string;
  readonly expectedUtilityState?: string;
  readonly funnelId?: string;
  readonly pages: readonly ExtractUtilityBillPage[];
  readonly userId?: number;
  readonly utilityAccountId?: number;
  readonly utilityId: number;
}

export interface ExtractedUtilityBillData {
  readonly accountNumber?: string | null;
  readonly address1?: string | null;
  readonly address2?: string | null;
  readonly annualUsageKwh?: number | null;
  readonly averageDailyUsageKwhYear?: number | null;
  readonly billDate?: string | null;
  readonly bill12MonthKwh?: number | null;
  readonly city?: string | null;
  readonly customerName?: string | null;
  readonly customerNumber?: string | null;
  readonly electricChoiceId?: string | null;
  readonly kwhConsumption?: number | null;
  readonly meterNumber?: string | null;
  readonly nameMatchReason?: string | null;
  readonly nameMatchesExpected?: boolean | null;
  readonly podNumber?: string | null;
  readonly premisesNumber?: string | null;
  readonly rateCode?: string | null;
  readonly residentialServiceNumber?: string | null;
  readonly serviceAddress?: string | null;
  readonly state?: string | null;
  readonly statementEnd?: string | null;
  readonly monthlyUsageKwh?: Partial<Record<UtilityBillUsageMonth, number | null>>;
  readonly zipCode?: string | null;
}

export interface ResolvedEstimatedUsage {
  readonly annualKwh: number;
  readonly dailyKwh: number;
  readonly dailyKwhDisplay: string;
}

export interface UtilityBillExtractionViolation {
  readonly field?: string | null;
  readonly message?: string | null;
  readonly messageKey?: string | null;
  readonly severity?: 'error' | 'warning' | null;
  readonly subtitle?: {
    readonly en?: string | null;
    readonly es?: string | null;
  } | null;
}

export interface UtilityBillValidationDisplayItem {
  readonly messageKey: string;
  readonly severity: 'error' | 'warning';
  readonly subtitle?: string;
  readonly title: string;
}

export interface ExtractUtilityBillResponse {
  readonly extractionData: ExtractedUtilityBillData;
  readonly id?: string | null;
  readonly qualificationStatus?: 'Qualified' | 'Needs Review' | 'Failed' | '' | null;
  readonly violations: readonly UtilityBillExtractionViolation[];
}

const ACCOUNT_NUMBER_KEY = 'account_number';
const ADDRESS_1_KEY = 'address_1';
const ADDRESS_2_KEY = 'address_2';
const ANNUAL_USAGE_KWH_KEY = 'annual_usage_kwh';
const AVERAGE_DAILY_USAGE_KWH_YEAR_KEY = 'average_daily_usage_kwh_year';
const BILL_DATE_KEY = 'bill_date';
const BILL_12_MONTH_KWH_KEY = 'bill_12_month_kwh';
const CUSTOMER_NAME_KEY = 'customer_name';
const CUSTOMER_NUMBER_KEY = 'customer_number';
const ELECTRIC_CHOICE_ID_KEY = 'electric_choice_id';
const EXTRACTION_DATA_KEY = 'extraction_data';
const EXTRACTION_REVIEW_KEY = 'extraction_review';
const KWH_CONSUMPTION_KEY = 'kwh_consumption';
const METER_NUMBER_KEY = 'meter_number';
const NAME_MATCH_REASON_KEY = 'name_match_reason';
const NAME_MATCHES_EXPECTED_KEY = 'name_matches_expected';
const POD_NUMBER_KEY = 'pod_number';
const PREMISES_NUMBER_KEY = 'premises_number';
const QUALIFICATION_STATUS_KEY = 'qualification_status';
const RATE_CODE_KEY = 'rate_code';
const RESIDENTIAL_SERVICE_NUMBER_KEY = 'residential_service_number';
const SERVICE_ADDRESS_KEY = 'service_address';
const STATEMENT_END_KEY = 'statement_end';
const ZIP_CODE_KEY = 'zip_code';
const NEEDS_REVIEW_KEY = 'needs_review';
const UTILITY_BILL_VALID_KEY = 'utility_bill_valid';
const STATUS_ERROR = 'error';
const DAILY_KWH_DECIMAL_PLACES = 2;

export const UTILITY_BILL_USAGE_MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

export type UtilityBillUsageMonth = (typeof UTILITY_BILL_USAGE_MONTHS)[number];

const knownMessageKeys = new Set([
  'api_error',
  'bad_bill',
  'expired_utility_bill',
  'invalid_utility_bill_date',
  'missing_required_data',
  'unsupported_document',
  'utility_bill_name_mismatch',
  'utility_bill_page_mismatch',
  'utility_bill_utility_mismatch',
  'utility_bill_valid',
  NEEDS_REVIEW_KEY,
]);

const blockingMessageKeys = new Set([
  'bad_bill',
  'expired_utility_bill',
  'invalid_utility_bill_date',
  'unsupported_document',
  'utility_bill_page_mismatch',
  'utility_bill_utility_mismatch',
]);

const apiTranslationKeyMessageMap: Record<string, string> = {
  AUTHORIZATION_INVALID: 'api_error',
  AUTHORIZATION_REQUIRED: 'api_error',
  BAD_REQUEST: 'api_error',
  FILE_REQUIRED: 'api_error',
  INTERNAL_SERVER_ERROR: 'api_error',
  INVALID_FILE: 'unsupported_document',
  INVALID_FILE_TYPE: 'unsupported_document',
  LIMIT_FILE_COUNT: 'unsupported_document',
  LIMIT_FILE_SIZE: 'unsupported_document',
  MISSING_FIELD_NAME: 'api_error',
  UNKNOWN_FILE_TYPE: 'unsupported_document',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const appendOptionalFormField = (
  formData: FormData,
  fieldName: string,
  value: number | string | undefined,
): void => {
  if (value !== undefined) {
    formData.append(fieldName, String(value));
  }
};

const dataUrlToBlob = (dataUrl: string, mimeType: string): Blob => {
  const base64Content = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  const binary = atob(base64Content);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
};

const toFormData = ({
  accountId,
  expectedCustomerName,
  expectedUtilityName,
  expectedUtilityState,
  funnelId,
  pages,
  userId,
  utilityAccountId,
  utilityId,
}: ExtractUtilityBillParams): FormData => {
  const formData = new FormData();

  appendOptionalFormField(formData, 'accountId', accountId);
  appendOptionalFormField(formData, 'expectedCustomerName', expectedCustomerName);
  appendOptionalFormField(formData, 'expectedUtilityName', expectedUtilityName);
  appendOptionalFormField(formData, 'expectedUtilityState', expectedUtilityState);
  appendOptionalFormField(formData, 'funnelId', funnelId);
  appendOptionalFormField(formData, 'userId', userId);
  appendOptionalFormField(formData, 'utilityAccountId', utilityAccountId);
  appendOptionalFormField(formData, 'utilityId', utilityId);

  pages.forEach(page => {
    formData.append(
      'files',
      page.file ?? dataUrlToBlob(page.base64Image, page.mimeType),
      page.fileName,
    );
  });

  return formData;
};

const toNullableZipCode = (value: unknown): string | null | undefined => {
  const zip = toNullableString(value);
  if (zip && zip.length > 15) return null;
  return zip;
};

const toNullableState = (value: unknown): string | null | undefined => {
  const state = toNullableString(value);
  if (state && state.length > 30) return null;
  return state;
};

const toMonthlyUsageKwh = (
  value: Record<string, unknown>,
): Partial<Record<UtilityBillUsageMonth, number | null>> | undefined => {
  const entries = UTILITY_BILL_USAGE_MONTHS.flatMap(month => {
    const usage = toNullableNumber(value[`kwh_consumption_${month}`]);
    return usage == null ? [] : ([[month, usage]] as const);
  });
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const toExtractionData = (
  value: unknown,
  annualUsageKwh?: number | null,
): ExtractedUtilityBillData => {
  if (!isRecord(value)) return {};

  return {
    accountNumber: toNullableString(value[ACCOUNT_NUMBER_KEY]),
    address1: toNullableString(value[ADDRESS_1_KEY]),
    address2: toNullableString(value[ADDRESS_2_KEY]),
    annualUsageKwh: annualUsageKwh ?? toNullableNumber(value.annualUsageKwh),
    averageDailyUsageKwhYear: toNullableNumber(value[AVERAGE_DAILY_USAGE_KWH_YEAR_KEY]),
    billDate: toNullableString(value[BILL_DATE_KEY]),
    bill12MonthKwh: toNullableNumber(value[BILL_12_MONTH_KWH_KEY]),
    city: toNullableString(value.city),
    customerName: toNullableString(value[CUSTOMER_NAME_KEY]),
    customerNumber: toNullableString(value[CUSTOMER_NUMBER_KEY]),
    electricChoiceId: toNullableString(value[ELECTRIC_CHOICE_ID_KEY]),
    kwhConsumption: toNullableNumber(value[KWH_CONSUMPTION_KEY]),
    meterNumber: toNullableString(value[METER_NUMBER_KEY]),
    nameMatchReason: toNullableString(value[NAME_MATCH_REASON_KEY]),
    nameMatchesExpected: toNullableBoolean(value[NAME_MATCHES_EXPECTED_KEY]),
    monthlyUsageKwh: toMonthlyUsageKwh(value),
    podNumber: toNullableString(value[POD_NUMBER_KEY]),
    premisesNumber: toNullableString(value[PREMISES_NUMBER_KEY]),
    rateCode: toNullableString(value[RATE_CODE_KEY]),
    residentialServiceNumber: toNullableString(value[RESIDENTIAL_SERVICE_NUMBER_KEY]),
    serviceAddress: toNullableString(value[SERVICE_ADDRESS_KEY]),
    state: toNullableState(value.state),
    statementEnd: toNullableString(value[STATEMENT_END_KEY]),
    zipCode: toNullableZipCode(value[ZIP_CODE_KEY]),
  };
};

const compactViolation = (
  violation: UtilityBillExtractionViolation,
): UtilityBillExtractionViolation => {
  return Object.fromEntries(
    Object.entries(violation).filter(([, value]) => value !== undefined),
  ) as UtilityBillExtractionViolation;
};

const toViolations = (value: unknown): readonly UtilityBillExtractionViolation[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(isRecord).map(violation =>
    compactViolation({
      field: toNullableString(violation.field),
      message: toNullableString(violation.message),
      messageKey: toNullableString(violation.messageKey) ?? toNullableString(violation.issue),
      severity:
        violation.severity === 'error' || violation.severity === 'warning'
          ? violation.severity
          : undefined,
      subtitle: isRecord(violation.subtitle)
        ? {
            en: toNullableString(violation.subtitle.en),
            es: toNullableString(violation.subtitle.es),
          }
        : undefined,
    }),
  );
};

const toQualificationStatus = (
  value: unknown,
): ExtractUtilityBillResponse['qualificationStatus'] => {
  if (
    value === 'Qualified' ||
    value === 'Needs Review' ||
    value === 'Failed' ||
    value === '' ||
    value === null
  ) {
    return value;
  }

  return undefined;
};

const toApiResults = (value: unknown): unknown => {
  if (!isRecord(value)) return value;
  return isRecord(value.results) ? value.results : value;
};

const normalizeViolationMessageKey = (messageKey: string | null | undefined): string => {
  if (!messageKey) return NEEDS_REVIEW_KEY;
  return knownMessageKeys.has(messageKey) ? messageKey : NEEDS_REVIEW_KEY;
};

const defaultSeverityForMessageKey = (
  messageKey: string,
): UtilityBillExtractionViolation['severity'] => {
  if (
    messageKey === 'bad_bill' ||
    messageKey === 'expired_utility_bill' ||
    messageKey === 'invalid_utility_bill_date' ||
    messageKey === 'utility_bill_page_mismatch' ||
    messageKey === 'utility_bill_utility_mismatch'
  ) {
    return 'error';
  }
  if (messageKey === 'unsupported_document') return 'error';
  return 'warning';
};

const hasTextValue = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const hasNumberValue = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const formatDailyKwh = (dailyKwh: number): string => {
  const multiplier = 10 ** DAILY_KWH_DECIMAL_PLACES;
  return (Math.floor(dailyKwh * multiplier) / multiplier).toFixed(DAILY_KWH_DECIMAL_PLACES);
};

/**
 * Prefill priority for enrollment estimated usage:
 * 1. bill-provided 12-month kWh -> daily = annual / 365
 * 2. estimated annual_usage_kwh (table- or chart-derived) -> daily = annual / 365
 * 3. last-year average daily (printed) -> annual = daily * 365
 */
export const resolveEstimatedUsage = (
  extractionData: Pick<
    ExtractedUtilityBillData,
    'averageDailyUsageKwhYear' | 'bill12MonthKwh' | 'annualUsageKwh'
  >,
): ResolvedEstimatedUsage | null => {
  let annualKwh: number | null = null;
  if (hasNumberValue(extractionData.bill12MonthKwh)) {
    annualKwh = extractionData.bill12MonthKwh;
  } else if (hasNumberValue(extractionData.annualUsageKwh)) {
    annualKwh = extractionData.annualUsageKwh;
  }

  if (annualKwh != null) {
    return {
      annualKwh,
      dailyKwh: annualKwh / 365,
      dailyKwhDisplay: formatDailyKwh(annualKwh / 365),
    };
  }

  if (hasNumberValue(extractionData.averageDailyUsageKwhYear)) {
    const dailyKwh = extractionData.averageDailyUsageKwhYear;
    return {
      dailyKwh,
      annualKwh: Math.round(dailyKwh * 365),
      dailyKwhDisplay: formatDailyKwh(dailyKwh),
    };
  }

  return null;
};

const hasSecondaryIdentifierText = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Map extracted secondary IDs onto the utility's secondary-identifier field.
 * Label keywords pick the best match; otherwise fall back to the first available value.
 */
export const resolveSecondaryIdentifierFromExtraction = (
  extractionData: Pick<
    ExtractedUtilityBillData,
    | 'customerNumber'
    | 'electricChoiceId'
    | 'podNumber'
    | 'premisesNumber'
    | 'residentialServiceNumber'
  >,
  secondaryFieldLabel?: string,
): string | null => {
  const label = secondaryFieldLabel?.trim().toLowerCase() ?? '';

  if (label.includes('electric choice') || label.includes('ecid')) {
    return hasSecondaryIdentifierText(extractionData.electricChoiceId)
      ? extractionData.electricChoiceId.trim()
      : null;
  }

  if (label.includes('customer number') || label.includes('customer')) {
    if (!hasSecondaryIdentifierText(extractionData.customerNumber)) {
      return null;
    }
    const customerNumber = extractionData.customerNumber.replace(/\s+/g, '');
    return customerNumber.length > 0 ? customerNumber : null;
  }

  if (label.includes('service number')) {
    if (!hasSecondaryIdentifierText(extractionData.residentialServiceNumber)) {
      return null;
    }
    const serviceNumber = extractionData.residentialServiceNumber.replace(/\s+/g, '');
    return serviceNumber.length > 0 ? serviceNumber : null;
  }

  if (label.includes('premises')) {
    return hasSecondaryIdentifierText(extractionData.premisesNumber)
      ? extractionData.premisesNumber.trim()
      : null;
  }

  if (label.includes('pod')) {
    return hasSecondaryIdentifierText(extractionData.podNumber)
      ? extractionData.podNumber.trim()
      : null;
  }

  if (hasSecondaryIdentifierText(extractionData.electricChoiceId)) {
    return extractionData.electricChoiceId.trim();
  }
  if (hasSecondaryIdentifierText(extractionData.premisesNumber)) {
    return extractionData.premisesNumber.trim();
  }
  if (hasSecondaryIdentifierText(extractionData.podNumber)) {
    return extractionData.podNumber.trim();
  }
  if (hasSecondaryIdentifierText(extractionData.customerNumber)) {
    return extractionData.customerNumber.trim();
  }
  if (hasSecondaryIdentifierText(extractionData.residentialServiceNumber)) {
    return extractionData.residentialServiceNumber.trim();
  }

  return null;
};

const hasAnyExtractionData = (extractionData: ExtractedUtilityBillData): boolean =>
  Object.values(extractionData).some(value => {
    if (typeof value === 'string') return value.trim().length > 0;
    return value !== null && value !== undefined;
  });

const toInferredViolations = (
  extractionData: ExtractedUtilityBillData,
): readonly UtilityBillExtractionViolation[] => {
  if (!hasAnyExtractionData(extractionData)) {
    return [{ messageKey: 'bad_bill', severity: 'error' }];
  }

  const hasAddress =
    hasTextValue(extractionData.serviceAddress) ||
    (hasTextValue(extractionData.address1) &&
      hasTextValue(extractionData.city) &&
      hasTextValue(extractionData.state) &&
      hasTextValue(extractionData.zipCode));
  const hasUsage =
    hasNumberValue(extractionData.averageDailyUsageKwhYear) ||
    hasNumberValue(extractionData.annualUsageKwh) ||
    hasNumberValue(extractionData.bill12MonthKwh) ||
    hasNumberValue(extractionData.kwhConsumption) ||
    Object.values(extractionData.monthlyUsageKwh ?? {}).some(
      usage => hasNumberValue(usage) && usage > 0,
    );

  if (
    !hasTextValue(extractionData.accountNumber) ||
    !hasTextValue(extractionData.customerName) ||
    !hasTextValue(extractionData.meterNumber) ||
    !hasAddress ||
    !hasUsage
  ) {
    return [{ messageKey: 'missing_required_data', severity: 'warning' }];
  }

  return [];
};

const hasNeedsReviewViolation = (violations: readonly UtilityBillExtractionViolation[]): boolean =>
  violations.some(
    violation => normalizeViolationMessageKey(violation.messageKey) === NEEDS_REVIEW_KEY,
  );

const hasBlockingViolation = (violations: readonly UtilityBillExtractionViolation[]): boolean =>
  violations.some(violation => {
    const messageKey = normalizeViolationMessageKey(violation.messageKey);
    return violation.severity === 'error' || blockingMessageKeys.has(messageKey);
  });

const shouldSuppressReviewMessages = ({
  apiViolations,
  extractionData,
}: {
  apiViolations: readonly UtilityBillExtractionViolation[];
  extractionData: ExtractedUtilityBillData;
}): boolean => hasBlockingViolation(apiViolations) || !hasAnyExtractionData(extractionData);

const withoutNeedsReviewViolations = (
  violations: readonly UtilityBillExtractionViolation[],
): readonly UtilityBillExtractionViolation[] =>
  violations.filter(
    violation => normalizeViolationMessageKey(violation.messageKey) !== NEEDS_REVIEW_KEY,
  );

const assembleViolations = ({
  apiViolations,
  extractionData,
  extractionReview,
  status,
}: {
  apiViolations: readonly UtilityBillExtractionViolation[];
  extractionData: ExtractedUtilityBillData;
  extractionReview: unknown;
  status: unknown;
}): readonly UtilityBillExtractionViolation[] => {
  const suppressReviewMessages = shouldSuppressReviewMessages({ apiViolations, extractionData });
  const reviewViolations =
    isRecord(extractionReview) && !suppressReviewMessages
      ? toViolations(extractionReview.cross_field_issues)
      : [];

  let violations: UtilityBillExtractionViolation[] = [...apiViolations, ...reviewViolations];

  if (
    isRecord(extractionReview) &&
    !suppressReviewMessages &&
    (extractionReview.overall_status === 'needs_review' ||
      extractionReview.overall_status === 'corrected') &&
    !hasNeedsReviewViolation(violations)
  ) {
    violations.push({ messageKey: NEEDS_REVIEW_KEY, severity: 'warning' });
  }

  if (status === STATUS_ERROR) {
    violations = [{ messageKey: 'api_error', severity: 'warning' }, ...violations];
  } else if (apiViolations.length === 0) {
    violations = [...violations, ...toInferredViolations(extractionData)];
  }

  if (hasBlockingViolation(violations)) {
    return withoutNeedsReviewViolations(violations);
  }

  if (violations.length === 0) {
    return [{ messageKey: UTILITY_BILL_VALID_KEY, severity: 'warning' }];
  }

  return violations;
};

const toExtractionResponse = (value: unknown): ExtractUtilityBillResponse => {
  const results = toApiResults(value);
  if (!isRecord(results)) {
    return {
      extractionData: {},
      violations: [{ messageKey: NEEDS_REVIEW_KEY, severity: 'warning' }],
    };
  }

  const extractionReview = results.extractionReview ?? results[EXTRACTION_REVIEW_KEY];
  const apiViolations = toViolations(results.violations);
  const extractionData = toExtractionData(
    results.extractionData ?? results[EXTRACTION_DATA_KEY],
    toNullableNumber(results.annualUsageKwh) ?? toNullableNumber(results[ANNUAL_USAGE_KWH_KEY]),
  );

  return {
    extractionData,
    id: toNullableString(results.id),
    qualificationStatus: toQualificationStatus(
      results.qualificationStatus ?? results[QUALIFICATION_STATUS_KEY],
    ),
    violations: assembleViolations({
      apiViolations,
      extractionData,
      extractionReview,
      status: results.status,
    }),
  };
};

const toApiErrorViolations = (value: unknown): readonly UtilityBillExtractionViolation[] => {
  if (!isRecord(value)) {
    return [{ messageKey: 'api_error', severity: 'warning' }];
  }

  const messageKeys = [
    apiTranslationKeyMessageMap[toNullableString(value.translationKey) ?? ''],
    ...(Array.isArray(value.validationErrors)
      ? value.validationErrors
          .filter(isRecord)
          .map(error => apiTranslationKeyMessageMap[toNullableString(error.translationKey) ?? ''])
      : []),
  ].filter((key): key is string => !!key);

  const uniqueKeys = Array.from(new Set(messageKeys.length > 0 ? messageKeys : ['api_error']));

  return uniqueKeys.map(messageKey => ({
    messageKey,
    severity: defaultSeverityForMessageKey(messageKey),
    message: toNullableString(value.message),
  }));
};

const getLocalizedViolationSubtitle = (
  violation: UtilityBillExtractionViolation,
  language: string,
): string | undefined => {
  const localizedSubtitle = language.startsWith('es')
    ? violation.subtitle?.es
    : violation.subtitle?.en;

  return localizedSubtitle || violation.message || undefined;
};

export const buildUtilityBillDisplayItems = (
  response: ExtractUtilityBillResponse,
  language: string,
): readonly UtilityBillValidationDisplayItem[] =>
  response.violations.map(violation => {
    const messageKey = normalizeViolationMessageKey(violation.messageKey);
    const severity = violation.severity ?? defaultSeverityForMessageKey(messageKey);

    return {
      messageKey,
      severity: severity === 'error' ? 'error' : 'warning',
      subtitle: getLocalizedViolationSubtitle(violation, language),
      title: getUtilityBillExtractionMessage(messageKey),
    };
  });

export const isBlockingUtilityBillResponse = (response: ExtractUtilityBillResponse): boolean =>
  response.violations.some(violation => {
    const messageKey = normalizeViolationMessageKey(violation.messageKey);
    return violation.severity === 'error' || blockingMessageKeys.has(messageKey);
  });

export const extractUtilityBill = async ({
  accountId,
  expectedCustomerName,
  expectedUtilityName,
  expectedUtilityState,
  funnelId,
  pages,
  userId,
  utilityAccountId,
  utilityId,
}: ExtractUtilityBillParams): Promise<ExtractUtilityBillResponse> => {
  if (!AI_VALIDATION_API_UTILITY_BILL_URL) {
    throw new Error('Utility bill extraction API URL is not configured.');
  }

  const token = store.get('TOKEN');
  const body = toFormData({
    accountId,
    expectedCustomerName,
    expectedUtilityName,
    expectedUtilityState,
    funnelId,
    pages,
    userId,
    utilityAccountId,
    utilityId,
  });

  const response = await fetch(AI_VALIDATION_API_UTILITY_BILL_URL, {
    body,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    method: 'POST',
  });
  const payload = (await response.json()) as unknown;

  if (!response.ok) {
    return {
      extractionData: {},
      violations: toApiErrorViolations(payload),
    };
  }

  return toExtractionResponse(payload);
};

export { getUtilityBillExtractionMessage, getUtilityBillExtractionMessages } from './messages';
